import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import { resolveProductIntakeApi } from "../../../utils/productIntakeApi";
import { toPlainWeightNumber } from "../order-view-items/orderItemDisplay";

export default function EditProductIntakeModal({
  open,
  handleClose,
  row,
  recordList,
  onSaveSuccess,
  intakeScope = "admin",
  isPersonalShopperItem = false,
}) {
  const intakeApi = resolveProductIntakeApi(intakeScope);
  const [quantity, setQuantity] = useState("");
  const [weight, setWeight] = useState("");
  const [price, setPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockedItemId, setLockedItemId] = useState(null);

  useEffect(() => {
    if (!open) {
      setLockedItemId(null);
      return;
    }
    if (!row?.id) return;
    setLockedItemId(row.id);
    const qty = row?.raw_quantity ?? row?.quantity;
    setQuantity(qty != null && qty !== "" && qty !== "-" ? String(qty) : "");
    const wt = row?.raw_weight ?? row?.weight;
    const plainWt = toPlainWeightNumber(wt);
    setWeight(plainWt != null ? String(plainWt) : "");
    const p = row?.price;
    setPrice(p != null && p !== "" && p !== "-" ? String(p) : "");
  }, [open, row?.id, row?.raw_quantity, row?.raw_weight, row?.product_name, row?.quantity, row?.weight, row?.price]);

  const handleSubmit = async () => {
    const itemId = lockedItemId || row?.id;
    if (!itemId) {
      toast.error("Product id is missing.");
      return;
    }

    const body = {};
    let hasField = false;

    if (quantity.trim() !== "") {
      const qty = parseInt(quantity, 10);
      if (Number.isNaN(qty) || qty < 1) {
        toast.error("Quantity must be at least 1.");
        return;
      }
      body.quantity = qty;
      hasField = true;
    }

    if (weight.trim() !== "") {
      const w = Number(weight);
      if (!Number.isFinite(w) || w < 0) {
        toast.error("Weight must be a valid number.");
        return;
      }
      body.weight = Math.round(w * 100) / 100;
      hasField = true;
    }

    if (isPersonalShopperItem && price.trim() !== "") {
      const p = Number(price);
      if (!Number.isFinite(p) || p < 0) {
        toast.error("Price must be a valid number.");
        return;
      }
      body.approx_rate = Math.round(p * 100) / 100;
      hasField = true;
    }

    if (!hasField) {
      toast.error("Update at least one field.");
      return;
    }

    try {
      setLoading(true);
      const response = await intakeApi.updateIntake({ id: itemId, ...body });
      const msg =
        response?.data?.data?.message ||
        response?.data?.message ||
        "Product updated successfully.";
      toast.success(msg);
      recordList?.();
      onSaveSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to update product.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={loading ? undefined : handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 570,
          maxWidth: "calc(100vw - 32px)",
          background: "#fff",
          borderRadius: "12px",
          outline: "none",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: "#126ABC",
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "16px" }}>
            {isPersonalShopperItem ? "Edit Qty / Weight / Price" : "Edit product intake"}
            {row?.product_name ? ` — ${row.product_name}` : ""}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "#fff", p: 0 }} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: "24px" }}>
          <Typography sx={{ fontWeight: 500, mb: 1, fontSize: "14px", color: "#333" }}>
            Quantity
          </Typography>
          <TextField
            fullWidth
            type="number"
            placeholder="Qty"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={loading}
            inputProps={{ min: 1 }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />

          <Typography sx={{ fontWeight: 500, mb: 1, fontSize: "14px", color: "#333" }}>
            Weight
          </Typography>
          <TextField
            fullWidth
            type="number"
            placeholder="Weight"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            disabled={loading}
            inputProps={{ min: 0, step: "any" }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />

          {isPersonalShopperItem && (
            <>
              <Typography sx={{ fontWeight: 500, mb: 1, fontSize: "14px", color: "#333" }}>
                Price
              </Typography>
              <TextField
                fullWidth
                type="number"
                placeholder="Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={loading}
                inputProps={{ min: 0, step: "any" }}
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                background: "#126ABC",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                "&:hover": { background: "#0f5a9e" },
              }}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
